import * as Sentry from '@sentry/react';

// Browser-side Sentry bootstrap. Called once at module load from
// src/Root.tsx (which only runs on the client because pages/index/
// +Page.tsx is client-only with ssr: false). Server-side Sentry init
// lives in src/sentryServer.ts and is preloaded via Node's --import
// flag — see src/server/instrument.ts.
//
// Sentry's hub model keeps the two SDKs coexisting cleanly: client
// errors flow through @sentry/react, server errors through @sentry/node,
// both into the same DSN and release tag.
export function initSentry() {
   if (typeof window === 'undefined') return;

   const isLocal = window.location.hostname === 'localhost';
   const dsn = import.meta.env.VITE_SENTRY_DSN;
   const mode = import.meta.env.MODE;
   const allowedModes = ['development', 'staging', 'production'];

   if (!dsn || isLocal || !allowedModes.includes(mode)) return;

   const release = `${import.meta.env.VITE_PACKAGE_NAME}@${import.meta.env.VITE_PACKAGE_VERSION}`;

   Sentry.init({
      dsn,
      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
      release,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: 1.0,
      sendDefaultPii: true,
      // Unactionable third-party/browser noise, triaged individually (Sentry
      // NPT-WEB-7J, 76, 7H, 7C, 7D, 63) — none have a first-party stack frame, so
      // there's no app code to fix. Ignoring keeps the issue stream signal-only.
      ignoreErrors: [
         // Google reCAPTCHA's own minified script throwing on internal state.
         "Cannot read properties of undefined (reading 'II')",
         // Hotjar's bundled script failing to read/write localStorage, usually
         // in Firefox private browsing or during pagehide teardown.
         'NS_ERROR_FAILURE',
         // Safari/iOS dropping its IndexedDB connection; self-resolves on reload.
         'Connection to Indexed Database server lost',
         // Known mapbox-gl-js bug: its session-telemetry request resolves after
         // map.remove() has already nulled out the callback it tries to invoke.
         'this.errorCb is not a function',
         // Vike's hydration-pristine-URL assertion — a benign race that can fire
         // if the URL changes a tick before hydration finishes; the page recovers.
         '[vike][Wrong Usage]',
         // The browser cancelling an in-flight XHR when the user navigates away
         // mid-request — expected, not a failed request our code needs to handle.
         'Request aborted',
      ],
   });
}
